import {
  customElement,
  DeesElement,
  property,
  state,
  html,
  cssManager,
  css,
  type TemplateResult,
} from '@design.estate/dees-element';

import { languageColor } from './dotglobal-language-colors.js';

interface ILangHover {
  lang: string;
  count: number;
  x: number;
}

/**
 * Language-composition bar with a hover speech-bubble tooltip (language name + share %).
 * Shared by the Explore org tiles and the Org view languages sidebar. With no items it
 * still renders the grey track, so orgs without detectable languages keep an empty bar.
 */
@customElement('dotglobal-langbar')
export class DotglobalLangbar extends DeesElement {
  @property({ type: Array })
  public accessor items: Array<[string, number]> = [];

  @property({ type: Number })
  public accessor barHeight = 6;

  @property({ type: Number })
  public accessor radius = 3;

  @state()
  private accessor hover: ILangHover | null = null;

  public static styles = [
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
      }
      .langbar-wrap {
        position: relative;
      }
      .langbar {
        display: flex;
        overflow: hidden;
        background: var(--bg-tertiary);
      }
      .langbar span {
        display: block;
        transition: filter 0.12s;
      }
      .langbar span:hover {
        filter: brightness(1.08);
      }
      .langtip {
        position: absolute;
        bottom: calc(100% + 9px);
        transform: translateX(-50%);
        display: inline-flex;
        align-items: center;
        gap: 7px;
        white-space: nowrap;
        padding: 5px 9px;
        font-family: var(--font-sans);
        font-size: 11.5px;
        font-weight: 600;
        color: var(--bg-primary);
        background: var(--text-primary);
        border-radius: 6px;
        box-shadow: var(--shadow-md);
        pointer-events: none;
        z-index: 30;
        transition: left 0.05s linear;
      }
      .langtip .lang-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        flex-shrink: 0;
      }
      .langtip .langtip-pct {
        font-weight: 500;
        opacity: 0.7;
      }
      .langtip::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 5px solid transparent;
        border-top-color: var(--text-primary);
      }
    `,
  ];

  public render(): TemplateResult {
    const total = this.items.reduce((sum, [, count]) => sum + count, 0) || 1;
    return html`
      <div class="langbar-wrap">
        <div
          class="langbar"
          style=${`height:${this.barHeight}px; border-radius:${this.radius}px`}
          @mousemove=${(eventArg: MouseEvent) => this.track(eventArg)}
          @mouseleave=${() => { this.hover = null; }}
        >
          ${this.items.map(([lang, count]) => html`
            <span data-lang=${lang} data-count=${count} style=${`flex:${count}; background:${languageColor(lang)}`}></span>
          `)}
        </div>
        ${this.hover
          ? html`
              <div class="langtip" style=${`left:${this.hover.x}px`}>
                <span class="lang-dot" style=${`background:${languageColor(this.hover.lang)}`}></span>
                ${this.hover.lang}
                <span class="langtip-pct">${Math.round((this.hover.count / total) * 100)}%</span>
              </div>
            `
          : ''}
      </div>
    `;
  }

  private track(eventArg: MouseEvent): void {
    const bar = eventArg.currentTarget as HTMLElement;
    const segment = (eventArg.target as HTMLElement).closest('span[data-lang]') as HTMLElement | null;
    if (!segment) {
      this.hover = null;
      return;
    }
    const rect = bar.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, eventArg.clientX - rect.left));
    this.hover = {
      lang: segment.dataset.lang ?? '',
      count: Number(segment.dataset.count ?? 0),
      x,
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dotglobal-langbar': DotglobalLangbar;
  }
}
